import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGetBulletinsV2Query } from '@/store/api';
import { 
  IBulletinListResponse, 
  IBulletinTableRow,
  IBulletinFilterModel,
  IBulletinSortModel,
  IBulletin,
  BULLETIN_FEED_FILTERS,
  BulletinFeedFilter
} from '@/types/admin/bulletins';
import { IPaginationModel } from '@/types/admin/dataTable';
import { useAdminUser } from './useAdminUser';
import { normalizeBulletinTableRow } from '@/utils/admin/bulletinUtils';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { ROWS_PER_PAGE } from '@/constants/admin';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';

export const useBulletins = () => {
  const adminUser = useAdminUser();
  
  // Table UI state via global table slice
  const table = useDataTableState('admin:bulletins');
  const [filterModel, setFilterModel] = useState<IBulletinFilterModel>({
    showScheduled: false,
    showDraft: false,
    recipientType: 'all',
    // Don't spread table.filterModel as it might have extra properties
  });
  const [sortModel, setSortModel] = useState<IBulletinSortModel[]>(
    (table.sortModel?.length ? table.sortModel : [{ field: 'createdAt', sort: 'desc' }]) as any
  );
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    ...(table.paginationModel || { page: 0, pageSize: ROWS_PER_PAGE }),
  });
  const [searchQuery, setSearchQuery] = useState<string>(table.searchQuery || "");

  // Feed filter state (Feed/Scheduled/Draft tabs)
  const [feedFilter, setFeedFilter] = useState<BulletinFeedFilter>(BULLETIN_FEED_FILTERS.FEED);

  // Debounce search query to prevent excessive API calls (300ms delay)
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // Error handling state
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // RTK Query hook - regular query for automatic cache synchronization
  const { 
    data, 
    isLoading, 
    isFetching,
    error,
    refetch,
  } = useGetBulletinsV2Query({
    sortModel,
    filterModel,
    paginationModel,
    selectedPropertyId: adminUser.selectedPropertyId!,
    searchQuery: debouncedSearchQuery.trim() || undefined,
  }, {
    skip: !adminUser.selectedPropertyId,
    // Automatically refetch when tags are invalidated (e.g., after edit/create/delete)
    refetchOnMountOrArgChange: true,
    // Force refetch when component regains focus
    refetchOnFocus: true,
    // Refetch when reconnecting
    refetchOnReconnect: true,
  });

  /**
   * Update filter model based on feed filter selection
   */
  const updateFilterModelFromFeedFilter = useCallback((filter: BulletinFeedFilter) => {
    setFilterModel(prev => ({
      ...prev,
      showScheduled: filter === BULLETIN_FEED_FILTERS.SCHEDULED,
      showDraft: filter === BULLETIN_FEED_FILTERS.DRAFT,
    }));
  }, []);

  /**
   * Handle errors from RTK Query
   */
  useEffect(() => {
    if (error) {
      const errorDetails = handleApiError(error, '/api/v2/bulletins', retryCount);
      setEnhancedError(errorDetails);
      setRetryCount(prev => prev + 1);
      console.error('🔴 Failed to fetch bulletins:', errorDetails);
    } else {
      // Clear error and reset retry count on successful fetch
      setEnhancedError(null);
      setRetryCount(0);
    }
  }, [error, retryCount]);

  /**
   * Safe data access with default values
   */
  const bulletinListData: IBulletinListResponse = useMemo(() => {
    if (!data || !data.data) {
      return { data: [], count: 0, hasMore: false };
    }
    return {
      data: data.data,
      count: data.count || data.data.length,
      hasMore: data.data.length === paginationModel.pageSize,
    };
  }, [data, paginationModel.pageSize]);

  /**
   * Whether the current view has finished fetching
   */
  const isCurrentViewSettled = useMemo(() => {
    return !isFetching;
  }, [isFetching]);

  /**
   * Normalized table rows for display
   */
  const tableRows = useMemo(() => {
    return bulletinListData?.data?.map(normalizeBulletinTableRow) || [];
  }, [bulletinListData]);

  /**
   * Loading state computation - only show loading on initial load or when no data exists
   */
  const isTableLoading = isLoading && (bulletinListData.data.length === 0);

  /**
   * Fetching-more state for pagination (in-flight requests when we already have data)
   */
  const isFetchingMore = isFetching && bulletinListData.data.length > 0;

  /**
   * Reset pagination when filters or sort change
   */
  useEffect(() => {
    if (paginationModel.page !== 0) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      table.setPagination({ ...paginationModel, page: 0 });
    }
  }, [filterModel, sortModel]);

  /**
   * Reset pagination when search changes
   */
  useEffect(() => {
    if (paginationModel.page !== 0) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      table.setPagination({ ...paginationModel, page: 0 });
    }
  }, [debouncedSearchQuery]);

  /**
   * Update feed filter and corresponding filter model
   */
  const updateFeedFilter = useCallback((filter: BulletinFeedFilter) => {
    setFeedFilter(filter);
    updateFilterModelFromFeedFilter(filter);
  }, [updateFilterModelFromFeedFilter]);

  /**
   * Update filter model
   */
  const updateFilterModel = useCallback((newFilterModel: IBulletinFilterModel) => {
    setFilterModel(newFilterModel);
    table.setFilter(newFilterModel as any);
  }, [table.setFilter]); // Use specific method instead of entire table object

  /**
   * Update sort model
   */
  const updateSortModel = useCallback((newSortModel: IBulletinSortModel[]) => {
    setSortModel(newSortModel);
    table.setSort(newSortModel as any);
  }, [table.setSort]); // Use specific method instead of entire table object

  /**
   * Update pagination
   */
  const updatePaginationModel = useCallback((newPaginationModel: IPaginationModel) => {
    setPaginationModel(newPaginationModel);
    table.setPagination(newPaginationModel);
  }, [table.setPagination]); // Use specific method instead of entire table object

  /**
   * Update search query
   */
  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    table.setSearch(query);
  }, [table.setSearch]); // Use specific method instead of entire table object

  /**
   * Go to next page
   */
  const goToNextPage = useCallback(() => {
    const maxPage = Math.ceil(bulletinListData.count / paginationModel.pageSize) - 1;
    if (paginationModel.page < maxPage) {
      setPaginationModel(prev => ({ ...prev, page: prev.page + 1 }));
    }
  }, [bulletinListData.count, paginationModel]);

  /**
   * Go to previous page
   */
  const goToPreviousPage = useCallback(() => {
    if (paginationModel.page > 0) {
      setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
    }
  }, [paginationModel.page]);

  /**
   * Check if there's a next page
   */
  const hasNextPage = useMemo(() => {
    const maxPage = Math.ceil(bulletinListData.count / paginationModel.pageSize) - 1;
    return paginationModel.page < maxPage;
  }, [bulletinListData.count, paginationModel]);

  /**
   * Check if there's a previous page
   */
  const hasPreviousPage = useMemo(() => {
    return paginationModel.page > 0;
  }, [paginationModel.page]);

  /**
   * Refresh data manually
   */
  const refresh = useCallback(() => {
    if (adminUser.selectedPropertyId) {
      refetch();
    }
  }, [adminUser.selectedPropertyId, refetch]);

  /**
   * Manual retry function for user-initiated retries
   */
  const manualRetry = useCallback(() => {
    setEnhancedError(null);
    setRetryCount(0);
    refetch();
  }, [refetch]);

  return {
    // Data
    data: bulletinListData,
    tableRows,
    
    // State
    isLoading: isTableLoading,
    isFetching,
    isFetchingMore,
    error: error ? String(error) : null,
    isCurrentViewSettled,
    
    // Enhanced Error Handling
    enhancedError,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
    manualRetry,
    
    // Pagination
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    
    // Feed Filter (Feed/Scheduled/Draft tabs)
    feedFilter,
    updateFeedFilter,
    
    // Filtering & Sorting
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    
    // Actions
    refresh,
    getBulletins: refetch, // For backward compatibility
  };
};
